import React from 'react';
import { ArrowRight, Shield, Sparkles, Zap } from 'lucide-react';
interface SecurityCTAProps {
  'data-id'?: string;
}
export const SecurityCTA: React.FC<SecurityCTAProps> = ({
  'data-id': dataId
}) => {
  return <section className="w-full bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] py-24 md:py-32 px-6 md:px-8" data-id="element-1646">
      <div className="w-full max-w-[1280px] mx-auto relative" data-id="element-1647">
        {/* White glow effect - matching pricing cards exactly */}
        <div className="absolute inset-0 rounded-[32px] blur-3xl bg-white opacity-30" style={{
        transform: 'scale(0.92)'
      }} data-id="element-1648" />

        {/* Main card with enhanced gradient border effect */}
        <div className="relative bg-gradient-to-br from-[#1a1a1a] via-[#1a1a1a] to-[#1a1a1a] rounded-[32px] px-8 md:px-12 py-12 md:py-16 border border-white/10 shadow-[0_8px_24px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.4)] overflow-hidden transition-all duration-500" data-id="element-1649">
          {/* Subtle grid pattern overlay */}
          <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} data-id="element-1650" />

          {/* Accent line at top */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-1.5 bg-gradient-to-r from-transparent via-[#3ab795] to-transparent rounded-full shadow-[0_0_20px_rgba(58,183,149,0.6)]" data-id="element-1651" />

          <div className="relative flex flex-col items-center gap-8 max-w-[700px] mx-auto text-center" data-id="element-1652">
            {/* Badge pill */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm" data-id="element-1653">
              <Sparkles className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-1654" />
              <span className="text-sm font-medium text-[#3ab795] uppercase tracking-wide" data-id="element-1655">
                Ready to Scale?
              </span>
            </div>

            {/* Heading with animated underline */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight" data-id="element-1656">
              <span className="text-white" data-id="element-1657">Automate Your Deal Flow. </span>
              <span className="relative inline-block text-[#3ab795]" data-id="element-1658">
                Close More Properties.
                <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-[#3ab795] to-[#2d9575] rounded-full shadow-[0_0_10px_rgba(58,183,149,0.5)]" data-id="element-1659" />
              </span>
            </h2>

            {/* Subheading */}
            <div className="flex flex-col items-center gap-4" data-id="element-1660">
              <p className="text-lg md:text-xl text-[#b8bcc8] leading-relaxed max-w-[600px]" data-id="element-1661">
                Join our exclusive beta and get early access to AcquireFlow.
                Help shape the future of automated real estate investing.
              </p>

              {/* Quick stats */}
              <div className="flex items-center gap-5 text-sm text-[#b8bcc8]" data-id="element-1662">
                <div className="flex items-center gap-1.5" data-id="element-1663">
                  <Zap className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-1664" />
                  <span className="font-medium" data-id="element-1665">1000+ offers/month</span>
                </div>
                <span className="text-[#3ab795]" data-id="element-1666">•</span>
                <div className="flex items-center gap-1.5" data-id="element-1667">
                  <Sparkles className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-1668" />
                  <span className="font-medium" data-id="element-1669">Limited spots</span>
                </div>
              </div>
            </div>

            {/* CTA Button with enhanced glow */}
            <a href="/trial" className="group relative inline-flex items-center gap-3 bg-[#3ab795] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#3ab795] focus:ring-offset-2 focus:ring-offset-[#1a1a1a] shadow-[0_0_30px_rgba(58,183,149,0.4),0_4px_16px_rgba(58,183,149,0.3)] hover:shadow-[0_0_50px_rgba(58,183,149,0.6),0_8px_24px_rgba(58,183,149,0.5)]" aria-label="Join our beta program" data-id="element-1670">
              {/* Button glow effect */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-[#3ab795] to-[#2d9575] opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-2xl -z-10" data-id="element-1671" />

              <span data-id="element-1672">Join Beta</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} data-id="element-1673" />
            </a>

            {/* Trust badges */}
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2" data-id="element-1674">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#3ab795]/10 border border-[#3ab795]/20" data-id="element-1675">
                <Sparkles className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-1676" />
                <span className="text-sm font-medium text-[#3ab795]" data-id="element-1677">
                  Limited beta spots
                </span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10" data-id="element-1678">
                <span className="text-sm text-[#b8bcc8]" data-id="element-1679">
                  Early access pricing
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};